import React, { useState, useEffect } from 'react';
import { Box, Typography, TextField, Button, CircularProgress, Link, Divider, IconButton, Alert } from '@mui/material';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import GoogleIcon from '@mui/icons-material/Google';
import MicrosoftIcon from '@mui/icons-material/Microsoft';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import VisibilityOutlined from '@mui/icons-material/VisibilityOutlined';
import VisibilityOffOutlined from '@mui/icons-material/VisibilityOffOutlined';
import AuthLayout, { authInputSx, authSubmitSx } from './AuthLayout';
import { auth } from '../../firebase';
import { GoogleAuthProvider, OAuthProvider, signInWithPopup } from 'firebase/auth';

const Register = () => {
  const { register, loading, isAuthenticated, setAuthFromServer } = useAuth();
  const navigate = useNavigate();
  const [formData, setFormData] = useState({ email: '', password: '' });
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [oauthLoading, setOauthLoading] = useState(null); // 'google' | 'microsoft' | null

  useEffect(() => {
    if (isAuthenticated && !loading) navigate('/dashboard');
  }, [isAuthenticated, loading, navigate]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((p) => ({ ...p, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!formData.email || !formData.password) return setError('Email and password required.');
    setSubmitting(true);
    try {
      const result = await register(formData.email, formData.password);
      if (result.success) navigate('/onboarding');
      else setError(result.error || 'Registration failed');
    } catch {
      setError('Unexpected error');
    } finally { setSubmitting(false); }
  };

  if (loading && !submitting) {
    return <Box sx={{ display:'flex', justifyContent:'center', alignItems:'center', minHeight:'100vh' }}><CircularProgress/></Box>;
  }
  if (isAuthenticated) return null;

  const exchange = async () => {
    if (!auth.currentUser) return;
    const idToken = await auth.currentUser.getIdToken();
    const API_BASE = `${process.env.REACT_APP_API_BASE || 'http://localhost:5000'}/api`;
    let data;
    try {
      const res = await fetch(`${API_BASE}/auth/firebase`, {
        method:'POST', headers:{ 'Content-Type':'application/json' }, credentials:'include', body: JSON.stringify({ idToken })
      });
      const text = await res.text();
      try { data = JSON.parse(text); } catch { throw new Error('Non-JSON response'); }
    } catch(err){
      console.error('Auth exchange error', err); setError('Network error reaching auth server'); return;
    }
    if (data.token && data.user) { setAuthFromServer(data.token, data.user); navigate('/onboarding'); }
    else setError(data.message || 'Third-party auth failed');
  };

  const signUpWithGoogle = async () => {
    try {
      setError(''); setOauthLoading('google');
      const provider = new GoogleAuthProvider();
      // Force account chooser so user can pick a different Google account each time
      provider.setCustomParameters({ prompt: 'select_account' });
      await signInWithPopup(auth, provider); await exchange();
    } catch(err){
      console.error('Google sign-up error', err);
      let friendly = 'Google sign-in failed';
      if (err?.code === 'auth/account-exists-with-different-credential') {
        friendly = 'This email already has an account with a different sign-in method. Sign in the way you originally signed up, then link Google from Settings.';
      }
      setError(friendly);
    } finally { setOauthLoading(null); }
  };

  const signUpWithMicrosoft = async () => {
    try { setError(''); setOauthLoading('microsoft'); const provider = new OAuthProvider('microsoft.com'); provider.addScope('email'); provider.addScope('profile'); await signInWithPopup(auth, provider); await exchange(); }
    catch(err){ console.error(err); setError('Microsoft sign-in failed'); }
    finally { setOauthLoading(null); }
  };

  return (
    <AuthLayout title="Create your account" subtitle="Free to start, takes less than a minute" busy={!!(submitting || oauthLoading)}>
      <Box sx={{ display:'flex', gap:1.2, flexDirection:{ xs:'column', sm:'row' }, mt:0.5 }}>
        <Button onClick={signUpWithGoogle} variant="outlined" startIcon={<GoogleIcon/>} disabled={!!oauthLoading || submitting} sx={{ flex:1, textTransform:'none', fontWeight:600, py:1 }}>{oauthLoading==='google'?<CircularProgress size={20}/>: 'Google'}</Button>
        <Button onClick={signUpWithMicrosoft} variant="outlined" startIcon={<MicrosoftIcon/>} disabled={!!oauthLoading || submitting} sx={{ flex:1, textTransform:'none', fontWeight:600, py:1 }}>{oauthLoading==='microsoft'?<CircularProgress size={20}/>: 'Microsoft'}</Button>
      </Box>
      <Divider sx={{ my:1.5 }}><Typography variant="caption" sx={{ letterSpacing:1 }}>OR</Typography></Divider>
      {error && <Alert severity="error" role="alert" sx={{ borderRadius: 2 }}>{error}</Alert>}
      <form onSubmit={handleSubmit} noValidate style={{ display:'flex', flexDirection:'column', gap:16 }}>
        <Box sx={{ display:'flex', flexDirection:'column', gap:1.75 }}>
          <Box sx={{ display:'flex', flexDirection:'column', gap:0.75 }}>
            <Typography variant="subtitle2" sx={{ fontWeight:600 }} component="label" htmlFor="reg-email">Email</Typography>
            <TextField id="reg-email" fullWidth name="email" type="email" placeholder="you@example.com" value={formData.email} onChange={handleChange} required size="small" autoComplete="email" autoFocus disabled={loading||submitting||!!oauthLoading} InputProps={{ sx: authInputSx }} />
          </Box>
          <Box sx={{ display:'flex', flexDirection:'column', gap:0.75 }}>
            <Typography variant="subtitle2" sx={{ fontWeight:600 }} component="label" htmlFor="reg-pass">Password</Typography>
            <TextField id="reg-pass" fullWidth name="password" type={showPassword?'text':'password'} placeholder="••••••••" value={formData.password} onChange={handleChange} required size="small" autoComplete="new-password" disabled={loading||submitting||!!oauthLoading} InputProps={{ endAdornment:(<IconButton size="small" onClick={()=>setShowPassword(p=>!p)} aria-label={showPassword?'Hide password':'Show password'} edge="end" sx={{ p:0.5 }}>{showPassword? <VisibilityOffOutlined sx={{ fontSize:18 }}/> : <VisibilityOutlined sx={{ fontSize:18 }}/>}</IconButton>), sx: authInputSx }} />
          </Box>
        </Box>
        <Button type="submit" fullWidth variant="contained" size="large" startIcon={<LockOutlinedIcon/>} disabled={loading||submitting||!!oauthLoading} sx={authSubmitSx}>{submitting? <CircularProgress size={22} color="inherit" /> : 'Sign up'}</Button>
      </form>
      <Typography variant="body2" color="text.secondary" sx={{ textAlign:'center', lineHeight:1.4, fontSize:'0.8125rem', mt:2 }}>
        By continuing you agree to our <Link component={RouterLink} to="/terms">Terms</Link> and <Link component={RouterLink} to="/privacy">Privacy Policy</Link>.
      </Typography>
      <Typography variant="body2" sx={{ textAlign:'center', fontSize:'0.8125rem', mt:1 }}>Already have an account? <Link component={RouterLink} to="/login" underline="hover" sx={{ fontWeight:600, fontSize:'0.8125rem' }}>Sign in</Link></Typography>
    </AuthLayout>
  );
};

export default Register;
